import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampus } from '../context/CampusContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowLeft, ArrowRight, Search, Sparkles, Loader2, Check,
  FileText, Edit3, Copy, Download, ChevronDown, ChevronUp,
  Shield, Database, Leaf, Wine, Trophy, Heart, GripVertical, Pencil, Trash2, Plus, X, RefreshCw,
  Building, Bot, GraduationCap, Globe, BookOpen, User, Users as UsersIcon,
  Briefcase, Code, BarChart, Monitor, Key, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const iconMap = {
  Shield: Shield,
  Database: Database,
  Leaf: Leaf,
  Wine: Wine,
  Trophy: Trophy,
  Heart: Heart,
  Building: Building,
  Bot: Bot,
  GraduationCap: GraduationCap,
  Globe: Globe,
  BookOpen: BookOpen,
  User: User,
  Users: UsersIcon,
  Briefcase: Briefcase,
  Code: Code,
  BarChart: BarChart,
  Monitor: Monitor,
  Key: Key,
  Zap: Zap
};

const CONTENT_TYPES = [
  'Artículo informativo',
  'Guía práctica',
  'Tutorial paso a paso',
  'Análisis en profundidad',
  'Caso de estudio',
  'Lista de consejos',
  'Comparativa',
  'Entrevista'
];

const STYLES = [
  'Informativo',
  'Persuasivo',
  'Narrativo',
  'Técnico',
  'Didáctico',
  'Analítico',
  'Descriptivo'
];

const TONES = [
  'Formal',
  'Informal',
  'Profesional',
  'Cercano',
  'Académico',
  'Divulgativo',
  'Inspirador'
];

const LANGUAGES = [
  { code: 'es', name: 'Español' },
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' }
];

// Sortable Index Item Component
const SortableIndexItem = ({ item, index, onEdit, onDelete, isEditing, editValue, onEditChange, onEditSave, onEditCancel }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 border rounded-lg transition-colors ${isDragging ? 'border-indigo-400 bg-indigo-50 shadow-lg' : 'border-slate-200 bg-white hover:border-slate-300'
        }`}
      data-testid={`index-item-${index}`}
    >
      {isEditing ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Título del apartado</Label>
            <Input
              value={editValue.title}
              onChange={(e) => onEditChange({ ...editValue, title: e.target.value })}
              className="h-9"
              data-testid={`edit-title-input-${index}`}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Descripción</Label>
            <Textarea
              value={editValue.description}
              onChange={(e) => onEditChange({ ...editValue, description: e.target.value })}
              className="min-h-[60px] resize-none"
              data-testid={`edit-description-input-${index}`}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditCancel}
              data-testid={`cancel-edit-btn-${index}`}
            >
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={onEditSave}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid={`save-edit-btn-${index}`}
            >
              <Check className="w-4 h-4 mr-1" />
              Guardar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <button
            {...attributes}
            {...listeners}
            className="flex-shrink-0 mt-1 p-1 rounded hover:bg-slate-100 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 transition-colors"
            data-testid={`drag-handle-${index}`}
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">
            {index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-900">{item.title}</h4>
            {item.description && (
              <p className="text-sm text-slate-500 mt-1">{item.description}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(index)}
              className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600"
              data-testid={`edit-btn-${index}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(index)}
              className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
              data-testid={`delete-btn-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const CreateContentPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { selectedCampus, campuses, setSelectedCampus, getCampusById } = useCampus();

  // Steps: 1 = Input, 2 = Index, 3 = Article
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    contentType: '',
    style: '',
    niche: '',
    subNiche: '',
    targetAudience: '',
    tone: '',
    language: 'es',
    campus: selectedCampus?.id || '',
    primaryKeywords: '',
    secondaryKeywords: ''
  });

  // State for each step
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [generatedIndex, setGeneratedIndex] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(false);

  // Index editing state
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState({ title: '', description: '' });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [developedSections, setDevelopedSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);
  const [regeneratingSection, setRegeneratingSection] = useState(-1);

  const [expandedSections, setExpandedSections] = useState({});

  // Campus niches state
  const [campusNiches, setCampusNiches] = useState([]);
  const [availableSubNiches, setAvailableSubNiches] = useState([]);

  // Load niches when campus changes
  const loadCampusNiches = async (campusId) => {
    if (!campusId) {
      setCampusNiches([]);
      setAvailableSubNiches([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/campuses/${campusId}/niches`,
        { headers: getAuthHeaders() }
      );
      setCampusNiches(response.data || []);
      setFormData(prev => ({ ...prev, niche: '', subNiche: '' }));
      setAvailableSubNiches([]);
    } catch (error) {
      console.error('Error loading niches:', error);
      setCampusNiches([]);
    }
  };

  // Handle campus change
  const handleCampusChange = (campusId) => {
    const campus = getCampusById(campusId);
    setSelectedCampus(campus);
    setFormData(prev => ({ ...prev, campus: campusId, niche: '', subNiche: '' }));
    loadCampusNiches(campusId);
  };

  // Handle niche change - update available subniches
  const handleNicheChange = (nicheName) => {
    setFormData(prev => ({ ...prev, niche: nicheName, subNiche: '' }));
    const selectedNiche = campusNiches.find(n => n.name === nicheName);
    setAvailableSubNiches(selectedNiche?.sub_niches || []);
  };

  // Search related questions via ValueSerp
  const searchRelatedQuestions = async () => {
    if (!formData.title.trim()) {
      toast.error('Introduce un título para buscar');
      return;
    }

    setLoadingQuestions(true);
    try {
      const response = await axios.post(
        `${API_URL}/search/related-questions`,
        {
          title: formData.title,
          language: formData.language
        },
        { headers: getAuthHeaders() }
      );
      setRelatedQuestions(response.data.related_questions);
      toast.success(`Se encontraron ${response.data.related_questions.length} preguntas relacionadas`);
    } catch (error) {
      console.error('Error searching questions:', error);
      toast.error('Error al buscar preguntas relacionadas');
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Toggle question selection
  const toggleQuestion = (question) => {
    setSelectedQuestions(prev =>
      prev.includes(question)
        ? prev.filter(q => q !== question)
        : [...prev, question]
    );
  };

  // Generate index
  const generateIndex = async () => {
    if (!formData.title || !formData.contentType || !formData.style || !formData.campus) {
      toast.error('Completa todos los campos obligatorios (incluyendo el Campus)');
      return;
    }

    // Parse keywords from comma-separated strings
    const primaryKeywords = formData.primaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    const secondaryKeywords = formData.secondaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    setLoadingIndex(true);
    try {
      const response = await axios.post(
        `${API_URL}/content/generate-index`,
        {
          title: formData.title,
          content_type: formData.contentType,
          style: formData.style,
          niche: formData.niche,
          sub_niche: formData.subNiche,
          target_audience: formData.targetAudience,
          tone: formData.tone,
          language: formData.language,
          related_questions: selectedQuestions,
          campus: formData.campus,
          primary_keywords: primaryKeywords,
          secondary_keywords: secondaryKeywords
        },
        { headers: getAuthHeaders() }
      );
      setGeneratedIndex(response.data.index.map((item, idx) => ({
        ...item,
        id: `index-${idx}-${Date.now()}`
      })));
      setCurrentStep(2);
      toast.success('Índice generado correctamente');
    } catch (error) {
      console.error('Error generating index:', error);
      const errorMsg = error.response?.data?.detail || 'Error al generar el índice';
      toast.error(errorMsg);
    } finally {
      setLoadingIndex(false);
    }
  };

  // Develop all sections
  const developAllSections = async () => {
    setLoadingSections(true);
    setCurrentStep(3);
    setDevelopedSections([]);

    // Parse keywords
    const primaryKeywords = formData.primaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    const secondaryKeywords = formData.secondaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    for (let i = 0; i < generatedIndex.length; i++) {
      setCurrentSectionIndex(i);
      try {
        const response = await axios.post(
          `${API_URL}/content/develop-section`,
          {
            section_title: generatedIndex[i].title,
            article_title: formData.title,
            content_type: formData.contentType,
            style: formData.style,
            niche: formData.niche,
            sub_niche: formData.subNiche,
            target_audience: formData.targetAudience,
            tone: formData.tone,
            language: formData.language,
            campus: formData.campus,
            primary_keywords: primaryKeywords,
            secondary_keywords: secondaryKeywords
          },
          { headers: getAuthHeaders() }
        );

        setDevelopedSections(prev => [...prev, {
          title: generatedIndex[i].title,
          content: response.data.content
        }]);

        // Auto-expand newly added section
        setExpandedSections(prev => ({ ...prev, [i]: true }));

      } catch (error) {
        console.error(`Error developing section ${i}:`, error);
        setDevelopedSections(prev => [...prev, {
          title: generatedIndex[i].title,
          content: `Error al generar esta sección. Por favor, intenta de nuevo.`
        }]);
      }
    }

    setCurrentSectionIndex(-1);
    setLoadingSections(false);
    toast.success('Artículo completo generado');
  };

  // Save article
  const saveArticle = async () => {
    try {
      const fullContent = developedSections.map(s =>
        `## ${s.title}\n\n${s.content}`
      ).join('\n\n---\n\n');

      await axios.post(
        `${API_URL}/articles`,
        {
          title: formData.title,
          campus: formData.campus,
          content_type: formData.contentType,
          style: formData.style,
          niche: formData.niche,
          sub_niche: formData.subNiche,
          target_audience: formData.targetAudience,
          tone: formData.tone,
          language: formData.language,
          index: generatedIndex,
          sections: developedSections,
          full_content: fullContent
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Artículo guardado correctamente');
      navigate('/history');
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Error al guardar el artículo');
    }
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const fullContent = `# ${formData.title}\n\n` + developedSections.map(s =>
      `## ${s.title}\n\n${s.content}`
    ).join('\n\n---\n\n');

    navigator.clipboard.writeText(fullContent);
    toast.success('Copiado al portapapeles');
  };

  // Download as markdown
  const downloadMarkdown = () => {
    const fullContent = `# ${formData.title}\n\n` + developedSections.map(s =>
      `## ${s.title}\n\n${s.content}`
    ).join('\n\n---\n\n');

    const blob = new Blob([fullContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.title.replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Descargando archivo...');
  };

  // Toggle section expansion
  const toggleSectionExpand = (index) => {
    setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Regenerate a single section
  const regenerateSection = async (index) => {
    const sectionTitle = generatedIndex[index].title;
    setRegeneratingSection(index);

    // Parse keywords
    const primaryKeywords = formData.primaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    const secondaryKeywords = formData.secondaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    try {
      const response = await axios.post(
        `${API_URL}/content/develop-section`,
        {
          section_title: sectionTitle,
          article_title: formData.title,
          content_type: formData.contentType,
          style: formData.style,
          niche: formData.niche,
          sub_niche: formData.subNiche,
          target_audience: formData.targetAudience,
          tone: formData.tone,
          language: formData.language,
          campus: formData.campus,
          primary_keywords: primaryKeywords,
          secondary_keywords: secondaryKeywords
        },
        { headers: getAuthHeaders() }
      );

      // Update the section
      setDevelopedSections(prev => prev.map((section, idx) =>
        idx === index
          ? { title: sectionTitle, content: response.data.content }
          : section
      ));

      toast.success('Sección regenerada');
    } catch (error) {
      console.error('Error regenerating section:', error);
      toast.error('Error al regenerar la sección');
    } finally {
      setRegeneratingSection(-1);
    }
  };

  // Handle drag end for index reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setGeneratedIndex((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      toast.success('Orden actualizado');
    }
  };

  // Edit index item
  const handleEditItem = (index) => {
    setEditingIndex(index);
    setEditValue({
      title: generatedIndex[index].title,
      description: generatedIndex[index].description || ''
    });
  };

  // Save edit
  const handleSaveEdit = () => {
    if (!editValue.title.trim()) {
      toast.error('El título no puede estar vacío');
      return;
    }
    setGeneratedIndex(prev => prev.map((item, idx) =>
      idx === editingIndex
        ? { ...item, title: editValue.title, description: editValue.description }
        : item
    ));
    setEditingIndex(null);
    setEditValue({ title: '', description: '' });
    toast.success('Apartado actualizado');
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue({ title: '', description: '' });
  };

  // Delete index item
  const handleDeleteItem = (index) => {
    if (generatedIndex.length <= 1) {
      toast.error('El índice debe tener al menos un apartado');
      return;
    }
    setGeneratedIndex(prev => prev.filter((_, idx) => idx !== index));
    toast.success('Apartado eliminado');
  };

  // Add new index item
  const handleAddItem = () => {
    const newItem = {
      id: `index-new-${Date.now()}`,
      title: 'Nuevo apartado',
      description: 'Descripción del apartado'
    };
    setGeneratedIndex(prev => [...prev, newItem]);
    // Automatically edit the new item
    setTimeout(() => {
      handleEditItem(generatedIndex.length);
    }, 100);
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step, idx) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div className={`step-circle ${currentStep > step ? 'completed' :
              currentStep === step ? 'active' : 'pending'
              }`}>
              {currentStep > step ? <Check className="w-4 h-4" /> : step}
            </div>
            <span className={`mt-2 text-xs font-medium ${currentStep >= step ? 'text-slate-900' : 'text-slate-400'
              }`}>
              {step === 1 ? 'Formulario' : step === 2 ? 'Índice' : 'Artículo'}
            </span>
          </div>
          {idx < 2 && (
            <div className={`step-line mx-4 ${currentStep > step ? 'completed' : 'pending'
              }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Input Form
  const renderStep1 = () => (
    <div className="space-y-8">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-indigo-600" />
            Datos del contenido
          </CardTitle>
          <CardDescription>
            Define los parámetros para generar tu artículo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Campus selector */}
          <div className="space-y-2">
            <Label>Campus *</Label>
            <Select
              value={formData.campus}
              onValueChange={handleCampusChange}
            >
              <SelectTrigger data-testid="campus-select" className="h-11">
                <SelectValue placeholder="Selecciona un campus" />
              </SelectTrigger>
              <SelectContent>
                {campuses.map(campus => {
                  const Icon = iconMap[campus.icon] || Building;
                  return (
                    <SelectItem key={campus.id} value={campus.id}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: campus.color }} />
                        {campus.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Title with search */}
          <div className="space-y-2">
            <Label htmlFor="title">Título del artículo *</Label>
            <div className="flex gap-2">
              <Input
                id="title"
                placeholder="Ej: Cómo implementar Zero Trust en tu empresa"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="title-input"
                className="h-11 flex-1"
              />
              <Button
                onClick={searchRelatedQuestions}
                disabled={loadingQuestions || !formData.title}
                variant="outline"
                className="h-11 px-4"
                data-testid="search-questions-btn"
              >
                {loadingQuestions ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Buscar preguntas
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Related questions */}
          {relatedQuestions.length > 0 && (
            <div className="space-y-3">
              <Label>Preguntas relacionadas (selecciona las que quieras incluir)</Label>
              <ScrollArea className="h-48 rounded-md border border-slate-200 p-4">
                <div className="space-y-2">
                  {relatedQuestions.map((question, idx) => (
                    <div
                      key={idx}
                      className={`question-toggle flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedQuestions.includes(question)
                        ? 'selected border-indigo-300 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                        }`}
                      onClick={() => toggleQuestion(question)}
                      data-testid={`question-${idx}`}
                    >
                      <Checkbox
                        checked={selectedQuestions.includes(question)}
                        onCheckedChange={() => toggleQuestion(question)}
                      />
                      <span className="text-sm text-slate-700">{question}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {selectedQuestions.length > 0 && (
                <p className="text-sm text-slate-500">
                  {selectedQuestions.length} pregunta(s) seleccionada(s)
                </p>
              )}
            </div>
          )}

          {/* Grid of selects */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Tipo de contenido *</Label>
              <Select
                value={formData.contentType}
                onValueChange={(v) => setFormData(prev => ({ ...prev, contentType: v }))}
              >
                <SelectTrigger data-testid="content-type-select" className="h-11">
                  <SelectValue placeholder="Selecciona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Estilo *</Label>
              <Select
                value={formData.style}
                onValueChange={(v) => setFormData(prev => ({ ...prev, style: v }))}
              >
                <SelectTrigger data-testid="style-select" className="h-11">
                  <SelectValue placeholder="Selecciona estilo" />
                </SelectTrigger>
                <SelectContent>
                  {STYLES.map(style => (
                    <SelectItem key={style} value={style}>{style}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Nicho</Label>
              {campusNiches.length > 0 ? (
                <Select
                  value={formData.niche}
                  onValueChange={handleNicheChange}
                >
                  <SelectTrigger data-testid="niche-select" className="h-11">
                    <SelectValue placeholder="Selecciona un nicho" />
                  </SelectTrigger>
                  <SelectContent>
                    {campusNiches.map(niche => (
                      <SelectItem key={niche.id || niche.name} value={niche.name}>{niche.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder={formData.campus ? "Sin nichos definidos - escribe uno" : "Selecciona primero un campus"}
                  value={formData.niche}
                  onChange={(e) => setFormData(prev => ({ ...prev, niche: e.target.value }))}
                  data-testid="niche-input"
                  className="h-11"
                  disabled={!formData.campus}
                />
              )}
              {formData.campus && campusNiches.length === 0 && (
                <p className="text-xs text-slate-500">Este campus no tiene nichos definidos. Puedes escribir uno manualmente o pedirle al admin que los configure.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Subnicho</Label>
              {availableSubNiches.length > 0 ? (
                <Select
                  value={formData.subNiche}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, subNiche: v }))}
                >
                  <SelectTrigger data-testid="subniche-select" className="h-11">
                    <SelectValue placeholder="Selecciona un subnicho" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSubNiches.map((subNiche, idx) => (
                      <SelectItem key={idx} value={subNiche}>{subNiche}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder={formData.niche ? "Sin subnichos definidos - escribe uno" : "Selecciona primero un nicho"}
                  value={formData.subNiche}
                  onChange={(e) => setFormData(prev => ({ ...prev, subNiche: e.target.value }))}
                  data-testid="subniche-input"
                  className="h-11"
                  disabled={!formData.niche}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>Público objetivo</Label>
              <Input
                placeholder="Ej: CTOs y responsables de seguridad"
                value={formData.targetAudience}
                onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
                data-testid="audience-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label>Tono</Label>
              <Select
                value={formData.tone}
                onValueChange={(v) => setFormData(prev => ({ ...prev, tone: v }))}
              >
                <SelectTrigger data-testid="tone-select" className="h-11">
                  <SelectValue placeholder="Selecciona tono" />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map(tone => (
                    <SelectItem key={tone} value={tone}>{tone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Idioma</Label>
              <Select
                value={formData.language}
                onValueChange={(v) => setFormData(prev => ({ ...prev, language: v }))}
              >
                <SelectTrigger data-testid="language-select" className="h-11">
                  <SelectValue placeholder="Selecciona idioma" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Keywords Section */}
          <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-slate-900">Palabras Clave SEO</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Palabras clave principales</Label>
                <Input
                  placeholder="Ej: ciberseguridad, zero trust, seguridad informática"
                  value={formData.primaryKeywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, primaryKeywords: e.target.value }))}
                  data-testid="primary-keywords-input"
                  className="h-11 bg-white"
                />
                <p className="text-xs text-slate-500">Separa con comas. Aparecerán en títulos y contenido principal.</p>
              </div>
              <div className="space-y-2">
                <Label>Palabras clave secundarias</Label>
                <Input
                  placeholder="Ej: protección de datos, firewall, autenticación"
                  value={formData.secondaryKeywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryKeywords: e.target.value }))}
                  data-testid="secondary-keywords-input"
                  className="h-11 bg-white"
                />
                <p className="text-xs text-slate-500">Separa con comas. Se integrarán de forma natural en el texto.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al dashboard
        </Button>
        <Button
          onClick={generateIndex}
          disabled={loadingIndex || !formData.title || !formData.contentType || !formData.style || !formData.campus}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="generate-index-btn"
        >
          {loadingIndex ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generando índice...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generar índice
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Step 2: Index Review
  const renderStep2 = () => (
    <div className="space-y-8">
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Índice generado
              </CardTitle>
              <CardDescription>
                Arrastra para reordenar, edita o elimina apartados
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddItem}
              data-testid="add-section-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Añadir apartado
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-4 bg-slate-50 rounded-lg">
            <h3 className="font-semibold text-slate-900 mb-1">{formData.title}</h3>
            <p className="text-sm text-slate-500">
              {formData.contentType} · {formData.style} · {selectedCampus?.name}
            </p>
            {(formData.primaryKeywords || formData.secondaryKeywords) && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                {formData.primaryKeywords && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    <span className="text-xs font-medium text-indigo-600 mr-1">Keywords principales:</span>
                    {formData.primaryKeywords.split(',').filter(k => k.trim()).map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">{kw.trim()}</span>
                    ))}
                  </div>
                )}
                {formData.secondaryKeywords && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs font-medium text-slate-500 mr-1">Keywords secundarias:</span>
                    {formData.secondaryKeywords.split(',').filter(k => k.trim()).map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">{kw.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
            <p className="text-sm text-indigo-700 flex items-center gap-2">
              <GripVertical className="w-4 h-4" />
              Arrastra los apartados para cambiar el orden
            </p>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={generatedIndex.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3" data-testid="index-list">
                {generatedIndex.map((item, idx) => (
                  <SortableIndexItem
                    key={item.id}
                    item={item}
                    index={idx}
                    onEdit={handleEditItem}
                    onDelete={handleDeleteItem}
                    isEditing={editingIndex === idx}
                    editValue={editValue}
                    onEditChange={setEditValue}
                    onEditSave={handleSaveEdit}
                    onEditCancel={handleCancelEdit}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(1)}
          data-testid="back-to-form-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Modificar datos
        </Button>
        <Button
          onClick={developAllSections}
          disabled={editingIndex !== null}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="develop-article-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Desarrollar artículo completo
        </Button>
      </div>
    </div>
  );

  // Step 3: Full Article
  const renderStep3 = () => (
    <div className="space-y-8">
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Artículo completo
              </CardTitle>
              <CardDescription>
                {loadingSections
                  ? `Generando sección ${currentSectionIndex + 1} de ${generatedIndex.length}...`
                  : `${developedSections.length} secciones generadas`
                }
              </CardDescription>
            </div>
            {!loadingSections && developedSections.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  data-testid="copy-article-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadMarkdown}
                  data-testid="download-article-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar MD
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-4 bg-slate-50 rounded-lg">
            <h3 className="font-semibold text-slate-900 text-xl mb-1">{formData.title}</h3>
            <p className="text-sm text-slate-500">
              {formData.contentType} · {formData.style} · {selectedCampus?.name}
            </p>
          </div>

          <div className="space-y-4" data-testid="article-sections">
            {generatedIndex.map((item, idx) => {
              const isLoading = loadingSections && idx === currentSectionIndex;
              const isDeveloped = developedSections[idx];
              const isExpanded = expandedSections[idx];

              return (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                  data-testid={`section-${idx}`}
                >
                  <div
                    className="p-4 bg-slate-50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => isDeveloped && toggleSectionExpand(idx)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">
                        {isDeveloped ? <Check className="w-4 h-4" /> : idx + 1}
                      </span>
                      <h4 className="font-medium text-slate-900">{item.title}</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      {isDeveloped && !loadingSections && regeneratingSection !== idx && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); regenerateSection(idx); }}
                          className="h-8 text-slate-500 hover:text-indigo-600"
                          data-testid={`regenerate-section-${idx}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Regenerar
                        </Button>
                      )}
                      {regeneratingSection === idx && (
                        <div className="flex items-center gap-2 text-indigo-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Regenerando...</span>
                        </div>
                      )}
                      {isDeveloped && regeneratingSection !== idx && (
                        isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                      {isLoading && <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />}
                    </div>
                  </div>

                  {isLoading && (
                    <div className="p-4 space-y-3">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                      <Skeleton className="h-4 w-4/6" />
                    </div>
                  )}

                  {isDeveloped && isExpanded && (
                    <div className="p-6 article-content border-t border-slate-100">
                      <div className="prose prose-slate max-w-none">
                        {isDeveloped.content.split('\n').map((para, pIdx) => (
                          para.trim() && <p key={pIdx} className="text-slate-700 leading-relaxed">{para}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(2)}
          disabled={loadingSections}
          data-testid="back-to-index-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al índice
        </Button>
        <Button
          onClick={saveArticle}
          disabled={loadingSections || developedSections.length === 0}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="save-article-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          Guardar artículo
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Crear Contenido</h1>
              <p className="text-xs text-slate-500">{selectedCampus?.name || 'Selecciona un campus'}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {renderStepIndicator()}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </main>
    </div>
  );
};

export default CreateContentPage;
